export const DEFAULT_VCHARTS_COLORS = [
  "#19d4ae",
  "#5ab1ef",
  "#fa6e86",
  "#ffb980",
  "#0067a6",
  "#c4b4e4",
  "#d87a80",
  "#9cbbff",
  "#d9d0c7",
  "#87a997",
  "#d49ea2",
  "#5b4947",
  "#7ba3a8",
];

const CHART_COLOR_KEY = /^chart-(\d+)$/;

/**
 * Series palette for ECharts. Uses the active theme's `chart-N` colors when it
 * defines any, otherwise DEFAULT_VCHARTS_COLORS. ECharts draws on a canvas and
 * cannot resolve theme tokens itself, so pass the result as raw colors.
 */
export function chartColors(theme) {
  const colors = theme.current.value.colors;
  const themed = Object.keys(colors)
    .map((key) => key.match(CHART_COLOR_KEY))
    .filter(Boolean)
    .sort((a, b) => a[1] - b[1])
    .map((match) => colors[match[0]]);
  return themed.length ? themed : DEFAULT_VCHARTS_COLORS;
}

// --- colors -----------------------------------------------------------------

/**
 * [r, g, b] (0-255) of a "#rgb" or "#rrggbb" color, null for anything else
 * (rgb(), a missing token, …).
 */
export function hexToRgb(color) {
  let hex = (color ?? "").replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    hex = [...hex].map((digit) => digit + digit).join("");
  }
  // an alpha channel (#rrggbbaa) is ignored
  if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex)) return null;
  return hex
    .slice(0, 6)
    .match(/../g)
    .map((pair) => parseInt(pair, 16));
}

/** "#rrggbb" of [r, g, b] (0-255) */
export function rgbToHex(rgb) {
  return (
    "#" +
    rgb
      .map((v) => Math.round(Math.min(255, Math.max(0, v))))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("")
  );
}

/** linear-light [r, g, b] (0-1) of a hex color, null when it is not one */
export function hexToLinearRgb(hex) {
  const rgb = hexToRgb(hex);
  return (
    rgb &&
    rgb.map((v) => {
      const c = v / 255;
      return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    })
  );
}

/**
 * "#rrggbb" of `color` mixed with `other`, `weight` (0-1) being the share of
 * `color`; null when either is not a hex color
 */
export function mixColors(color, other, weight) {
  const a = hexToRgb(color);
  const b = hexToRgb(other);
  if (!a || !b) return null;
  return rgbToHex(a.map((v, i) => v * weight + b[i] * (1 - weight)));
}

/** WCAG relative luminance of a hex color, null when it is not one */
function luminance(hex) {
  const rgb = hexToLinearRgb(hex);
  return rgb && 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

/** WCAG contrast ratio (1-21) of two hex colors, null when one is not one */
export function contrastRatio(color, other) {
  const a = luminance(color);
  const b = luminance(other);
  if (a === null || b === null) return null;
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

// --- sequential ramp (heatmaps) ---------------------------------------------

// ECharts' own default visualMap ramp
const DEFAULT_GRADIENT = ["#f6efa6", "#d88273", "#bf444c"];

function linearRgbToHex(rgb) {
  return rgbToHex(
    rgb.map((c) => {
      const v = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
      return Math.min(1, Math.max(0, v)) * 255;
    }),
  );
}

function linearRgbToOklab([r, g, b]) {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function oklabToLinearRgb([L, a, b]) {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/**
 * Sequential color ramp for heatmaps, built from theme tokens:
 * surface-container-lowest -> primary-container (at `midStop`) -> primary,
 * interpolated in OKLab so the steps stay roughly even in lightness.
 * ECharts blends between visualMap colors in plain RGB, so the ramp is
 * sampled into `steps` stops to keep that blending close to OKLab.
 */
export function sequentialRamp(theme, { steps = 9, midStop = 0.3 } = {}) {
  const colors = theme.current.value.colors;
  const linear = [
    [0, hexToLinearRgb(colors["surface-container-lowest"])],
    [midStop, hexToLinearRgb(colors["primary-container"])],
    [1, hexToLinearRgb(colors.primary)],
  ];
  // a token that is missing or not a hex color can't be interpolated
  if (linear.some(([, rgb]) => !rgb)) return DEFAULT_GRADIENT;
  const stops = linear.map(([t, rgb]) => [t, linearRgbToOklab(rgb)]);

  return Array.from({ length: steps }, (_, i) => {
    const t = i / (steps - 1);
    const k = t <= midStop ? 0 : 1;
    const [t0, from] = stops[k];
    const [t1, to] = stops[k + 1];
    const f = (t - t0) / (t1 - t0);
    const lab = from.map((v, j) => v + (to[j] - v) * f);
    return linearRgbToHex(oklabToLinearRgb(lab));
  });
}
